// 編輯器底圖（SPEC #ls2505-334「底圖」）：先顯示這台最新的事件快照，使用者按〔看即時〕才開即時影像
// （NVR 同時觀看的路數有限，畫區域不該佔一路）。沒有快照 → 灰底 + 「按〔看即時〕載入畫面」。
// 即時影像用子碼流、不協商音訊；關掉編輯器 = 卸載 = 收掉串流並結束 VMS session。
import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveView, vms, vmsReserve } from '@kinio/web-sdk';
import { deviceApi } from '../../../lib/deviceApi';
import type { VmsDevice } from '../../../lib/devices';

export type BackdropState = 'loading' | 'snapshot' | 'none' | 'connecting' | 'playing' | 'unavailable';

/** 按〔看即時〕後這麼久還沒有影格 → 當作連不上（灰底 + 提示，仍可編輯）。 */
export const LIVE_TIMEOUT_MS = 15_000;

export function useBackdrop(dev: Pick<VmsDevice, 'apiEndpoint' | 'homeId' | 'aid'>) {
  const [state, setState] = useState<BackdropState>('loading');
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [live, setLive] = useState(0); // 0 = 沒開；每按一次〔看即時〕/ 重試 +1
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    let done = false;
    deviceApi
      .latestSnapshot(dev.apiEndpoint, dev.homeId, dev.aid)
      .then((s) => {
        if (done || liveRef.current) return;
        setSnapshotUrl(s.url);
        setState('snapshot');
      })
      .catch(() => {
        if (!done && !liveRef.current) setState('none');
      });
    return () => {
      done = true;
    };
  }, [dev.apiEndpoint, dev.homeId, dev.aid]);

  useEffect(() => {
    if (!live) return;
    let done = false;
    setState('connecting');
    const timer = setTimeout(() => {
      if (!done) setState((s) => (s === 'playing' ? s : 'unavailable'));
    }, LIVE_TIMEOUT_MS);
    const lv = new LiveView({
      apiEndpoint: dev.apiEndpoint,
      homeId: dev.homeId,
      deviceRef: dev.aid,
      audio: false,
      talk: false,
      stream: 'sub',
      reserve: vmsReserve(dev.apiEndpoint, dev.homeId, dev.aid, 'sub'),
      onStream: (s) => {
        if (!done) setStream(s);
      },
      onError: () => {
        if (!done) setState((s) => (s === 'playing' ? s : 'unavailable'));
      },
    });
    void lv.start();
    return () => {
      done = true;
      clearTimeout(timer);
      const sid = lv.reservedSessionId;
      lv.dispose();
      if (sid) void vms.endSession(dev.apiEndpoint, dev.homeId, sid).catch(() => undefined);
      setStream(null);
    };
  }, [live, dev.apiEndpoint, dev.homeId, dev.aid]);

  const startLive = useCallback(() => setLive((n) => n + 1), []);
  const markPlaying = useCallback(() => setState('playing'), []);
  return { state, snapshotUrl, stream, startLive, markPlaying, liveRequested: live > 0 };
}
